import { REQUIRED_ROLE } from '../auth/claims'

/**
 * The staff role shown under the name in the header identity cluster.
 *
 * Its own module rather than a helper inside `header-bar.tsx` for one
 * practical reason: exporting a non-component from a file that exports a
 * component breaks React Fast Refresh, and Vite says so out loud
 * ("roleLabel export is incompatible"). Every edit to the header would cost
 * a full page reload.
 *
 * This is deliberately NOT in `auth/claims.ts`. Claims are the token's
 * shape and the authorisation decision; what a role is called in English is
 * presentation, and the two change for different reasons.
 */

/**
 * Human labels for the roles this application knows about, ordered
 * most-privileged first. **That order is the selection rule**: a token can
 * carry several roles, and `roles[0]` is not a defensible choice because
 * Entra gives no ordering guarantee on the claim. We show the first entry
 * below that the user actually holds, so two staff with the same top role
 * always see the same word, run to run.
 *
 * Only the four identifiers the code already knows are here: `bo.staff`
 * (`REQUIRED_ROLE`) and the three in `MURMURATION_ROLES`. The wider role
 * set is deliberately absent. MUR Open Design Questions Q3 records that it
 * does not agree with itself - Roles and Permissions lists 16 roles, the
 * Master Feature List says 17, and Board member and Auditor appear only in
 * the Treasury permissions matrix - and Q3 is owned by `business-architect`.
 * Reconcile it there. Inventing "Branch manager" here to match the mockup
 * would settle Q3 by accident, in a file nobody would think to look in.
 */
const ROLE_LABELS: ReadonlyArray<{ readonly role: string; readonly label: string }> = [
  { role: 'mur.super_admin', label: 'Super administrator' },
  { role: 'mur.platform_admin', label: 'Platform administrator' },
  { role: 'mur.support', label: 'Murmuration support' },
  { role: REQUIRED_ROLE, label: 'Back-office staff' },
]

/**
 * The one role to show for a staff member, or `null` when none of their
 * roles is one we can name.
 *
 * `null` - rendering no second line - is the deliberate choice over the two
 * alternatives. A raw `bo.staff` in permanent chrome is a machine
 * identifier, not a human label; and a guessed label is worse than a blank,
 * because a guess reads as authoritative.
 */
export function staffRoleLabel(roles: readonly string[]): string | null {
  return ROLE_LABELS.find((entry) => roles.includes(entry.role))?.label ?? null
}
